import { FC, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "store/hooks";
import { getTestTypeValue } from "store/slices/test";
import { getTestTypeLocaleKey } from "utils/testType.utils";
import { useTheme } from "@mui/material/styles";

import YouTube from "react-youtube";
import MDButton from "components/MDButton";
import IconBackground from "assets/Icons/iconBackground.svg?react";
import Attention from "assets/Icons/attention.svg?react";

import { makeStyles } from "tss-react/mui";
import { styles } from "./InstractionsScreen.styles";
import { sentryLogMessage } from "services/sentryLogger";
import { SENTRY_EVENT_TYPES } from "constants/sentryEvents";

interface StepProps {
  handleUpdateStep: (step: number) => void;
}

const InstractionsScreen: FC<StepProps> = ({ handleUpdateStep }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const testType = useAppSelector(getTestTypeValue);
  const testTypeLocaleKey = getTestTypeLocaleKey(testType);
  const [youTubeOptions, setYouTubeOptions] = useState();
  const useStyles = makeStyles()(() => styles(theme));
  const { classes } = useStyles();

  useEffect(() => {
    const container = document.getElementById("instractionsScreenContainer");
    if (!container) {
      return;
    }
    const youTubePlayerWidth = container.clientWidth;
    const options: any = {
      height: 216,
      width: youTubePlayerWidth,
    };
    setYouTubeOptions(options);
  }, []);

  const onReady = (event: any) => {
    event.target.pauseVideo();
  };
  const onVideoPlayedHandler = (event: any) => {
    event.target.playVideo();
    sentryLogMessage({
      message: 'InstractionsScreen video played',
      level: 'info',
      tags: {
        component: 'InstractionsScreen',
        category: 'video_played',
        event_type: SENTRY_EVENT_TYPES.VIEWED_TUTORIAL_VIDEO,
      },
      extra: {
        videoId: t(`${testTypeLocaleKey}:InstractionsScreen:youtubeVideo`),
        timestamp: new Date().toISOString(),
      },
    });
  };
  const handleSubmit = () => {
    handleUpdateStep(2);
  };

  return (
    <div id="instractionsScreenContainer" className={classes.container}>
      <div className={classes.bodyContainer}>
      <div className={classes.warningTitle}>{t(`${testTypeLocaleKey}:InstractionsScreen:warningTitle`)}</div>
        <div className={classes.content}>
          {t(`${testTypeLocaleKey}:InstractionsScreen:extraInfo`)}
          <div className={classes.detailsTitle}>{t(`${testTypeLocaleKey}:InstractionsScreen:detailsTitle`)}</div>
          <div>&#9679; {t(`${testTypeLocaleKey}:InstractionsScreen:case1`)}</div>
          <div>&#9679; {t(`${testTypeLocaleKey}:InstractionsScreen:case2`)}</div>
          <div>&#9679; {t(`${testTypeLocaleKey}:InstractionsScreen:case3`)}</div>
          <div>&#9679; {t(`${testTypeLocaleKey}:InstractionsScreen:case4`)}</div>
        </div>
        <div className={classes.youTubeContainer}>
          {youTubeOptions ? (
            <YouTube
              iframeClassName={classes.iframeClassName}
              videoId={t(`${testTypeLocaleKey}:InstractionsScreen:youtubeVideo`)}
              opts={youTubeOptions}
              onReady={onReady}
              onPlay={onVideoPlayedHandler}
            />
          ) : null}
        </div>
        <div className={classes.attentionContainer}>
          <div className={classes.iconBackground}>
            <IconBackground />
            <div className={classes.attentionIcon}>
              <Attention className={classes.attentionIconImage} />
            </div>
          </div>
          <div className={classes.detailsContainer}>
            <div className={classes.attentionTitle}>
              {t(`${testTypeLocaleKey}:InstractionsScreen:attentionTitle`)}
            </div>
            <div className={classes.attentionDescription}>
              {t(`${testTypeLocaleKey}:InstractionsScreen:attentionDescription`)}
            </div>
          </div>
        </div>
      </div>

      <MDButton
        style={{ width: theme.width.full, height: theme.spacing.spacing28 }}
        color={"primary"}
        variant="contained"
        type="submit"
        onClick={handleSubmit}
      >
        {t(`${testTypeLocaleKey}:InstractionsScreen:nextButton`)}
      </MDButton>
    </div>
  );
};

export default InstractionsScreen;
